import React from "react";
import { Link } from "@/lib/router-compat";
import { ArrowRight, Sun, Phone, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";

const BRIGHTER_TOMORROW_IMG =
  "https://vibe.filesafe.space/1787327131284421342/attachments/c6509b2d-5d33-430c-89a3-413814487aa0.png";

export const BrighterTomorrowBanner: React.FC = () => {
  return (
    <section
      aria-label="A Brighter Tomorrow Is Possible"
      className="relative overflow-hidden bg-gradient-hero py-16 sm:py-20 lg:py-24 border-b border-border"
    >
      <div className="container-px mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid items-center gap-10 lg:grid-cols-12 lg:gap-14">
          {/* Visual Column */}
          <div className="lg:col-span-6 order-2 lg:order-1">
            <div className="relative overflow-hidden rounded-2xl border border-border bg-card p-2 sm:p-3 shadow-premium transition-transform hover:scale-[1.01] duration-300">
              <img
                src={BRIGHTER_TOMORROW_IMG}
                alt="A Brighter Tomorrow Is Possible — Advanced care for depression, anxiety, and PTSD at Neuro Wellness"
                className="w-full h-auto rounded-xl object-contain"
                loading="lazy"
                width={1200}
                height={900}
              />
            </div>
          </div>

          {/* Text & Message Column */}
          <div className="lg:col-span-6 space-y-6 order-1 lg:order-2">
            <div className="inline-flex items-center gap-2 rounded-full border border-accent/30 bg-accent/10 px-3.5 py-1 text-xs sm:text-sm font-semibold uppercase tracking-wider text-accent">
              <Sun className="h-4 w-4" />
              Hope Beyond Medication
            </div>

            <h2 className="font-headings text-2xl sm:text-3xl lg:text-4xl font-extrabold text-primary tracking-tight">
              A Brighter Tomorrow Is Possible
            </h2>

            <p className="text-base sm:text-lg text-foreground/85 leading-relaxed">
              Advanced, FDA-cleared neuromodulation for depression, anxiety, and
              PTSD. If you’ve spent months or years feeling stuck in a treatment
              rut, our team provides an environment of calm, privacy, and
              clinical excellence to help you reclaim your life.
            </p>

            <div className="space-y-3 pt-1">
              {[
                "Personalized care tailored to your unique goals and clinical history",
                "Private, comfortable outpatient suites designed for your peace of mind",
                "Accelerated 5-day protocols and comprehensive ongoing support",
                "Appointments available seven days a week with extended hours",
              ].map((text) => (
                <div key={text} className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 shrink-0 text-accent mt-0.5" />
                  <span className="text-sm sm:text-base text-foreground/80">
                    {text}
                  </span>
                </div>
              ))}
            </div>

            <div className="pt-3 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <Button
                asChild
                size="lg"
                className="bg-gradient-accent text-accent-foreground font-semibold shadow-glow hover:opacity-90 text-sm sm:text-base"
              >
                <a href="#consultation">
                  <span>Schedule Consultation</span>
                  <ArrowRight className="h-4 w-4 ml-2" />
                </a>
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="border-primary/30 text-primary hover:bg-primary/5 font-semibold text-sm sm:text-base"
              >
                <a href="tel:9548279793">
                  <Phone className="h-4 w-4 mr-2" />
                  Call 954-827-9793
                </a>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BrighterTomorrowBanner;
